import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Wallet, Plus, Edit, Search, TrendingUp, DollarSign, Clock, CheckCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EarningsManagement = () => {
  const [earnings, setEarnings] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEarning, setEditingEarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    userId: '',
    totalEarnings: '',
    pendingPayout: '',
    completedPayout: '',
    commissionRate: '10',
    payoutMethod: 'UPI',
    lastPayoutDate: ''
  });

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/admin/earnings`);
      setEarnings(response.data || []);
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchEarnings();
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingEarning) {
        await axios.put(`${API}/admin/earnings/${editingEarning.earningId || editingEarning.id}`, formData);
        toast({ title: '✅ Success', description: 'Earnings updated successfully' });
      } else {
        await axios.post(`${API}/admin/earnings`, formData);
        toast({ title: '✅ Success', description: 'Earnings record created successfully' });
      }
      setDialogOpen(false);
      setEditingEarning(null);
      resetForm();
      fetchEarnings();
    } catch (error) {
      toast({ 
        title: '❌ Error', 
        description: error.response?.data?.detail || 'Operation failed', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      userId: '',
      totalEarnings: '',
      pendingPayout: '',
      completedPayout: '',
      commissionRate: '10',
      payoutMethod: 'UPI',
      lastPayoutDate: ''
    });
  };

  const handleEdit = (earning) => {
    setEditingEarning(earning);
    setFormData({
      userId: earning.userId,
      totalEarnings: earning.totalEarnings?.toString() || '0',
      pendingPayout: earning.pendingPayout?.toString() || '0',
      completedPayout: earning.completedPayout?.toString() || '0',
      commissionRate: earning.commissionRate?.toString() || '10',
      payoutMethod: earning.payoutMethod || 'UPI',
      lastPayoutDate: earning.lastPayoutDate ? new Date(earning.lastPayoutDate).toISOString().slice(0, 10) : ''
    });
    setDialogOpen(true);
  };

  const filteredEarnings = earnings.filter(earning => {
    const search = searchTerm.toLowerCase();
    return (
      earning.userName?.toLowerCase().includes(search) ||
      earning.userEmail?.toLowerCase().includes(search) ||
      earning.clientId?.toString().includes(search) ||
      earning.earningId?.toLowerCase().includes(search)
    );
  });

  // Calculate totals for stats
  const totalEarningsSum = earnings.reduce((sum, e) => sum + (e.totalEarnings || 0), 0);
  const totalPendingSum = earnings.reduce((sum, e) => sum + (e.pendingPayout || 0), 0);
  const totalCompletedSum = earnings.reduce((sum, e) => sum + (e.completedPayout || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-blue-600">Earnings Management</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => { resetForm(); setEditingEarning(null); }} 
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-green-500/50 transition-all hover:scale-105"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Earnings
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-green-600 text-xl">{editingEarning ? 'Edit Earnings' : 'Create New Earnings Record'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-slate-600 font-medium">User</Label>
                <Select 
                  value={formData.userId} 
                  onValueChange={(value) => setFormData({ ...formData, userId: value })} 
                  disabled={!!editingEarning}
                >
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800 mt-2">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.principalId} value={user.principalId}>
                        #{user.clientId} - {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 font-medium">Total Earnings (₹)</Label>
                  <Input
                    type="number"
                    value={formData.totalEarnings}
                    onChange={(e) => setFormData({ ...formData, totalEarnings: e.target.value })}
                    placeholder="0"
                    className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 font-medium">Commission Rate (%)</Label>
                  <Input
                    type="number"
                    value={formData.commissionRate}
                    onChange={(e) => setFormData({ ...formData, commissionRate: e.target.value })}
                    placeholder="10"
                    className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 font-medium">Pending Payout (₹)</Label>
                  <Input
                    type="number"
                    value={formData.pendingPayout}
                    onChange={(e) => setFormData({ ...formData, pendingPayout: e.target.value })}
                    placeholder="0"
                    className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 font-medium">Completed Payout (₹)</Label>
                  <Input
                    type="number"
                    value={formData.completedPayout}
                    onChange={(e) => setFormData({ ...formData, completedPayout: e.target.value })}
                    placeholder="0"
                    className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 font-medium">Payout Method</Label>
                  <Select value={formData.payoutMethod} onValueChange={(value) => setFormData({ ...formData, payoutMethod: value })}>
                    <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800 mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600 font-medium">Last Payout Date</Label>
                  <Input
                    type="date"
                    value={formData.lastPayoutDate}
                    onChange={(e) => setFormData({ ...formData, lastPayoutDate: e.target.value })}
                    className="bg-slate-50 border-slate-200 text-slate-800 mt-2"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg hover:shadow-green-500/50 transition-all"
              >
                {loading ? 'Processing...' : editingEarning ? 'Update Earnings' : 'Create Earnings Record'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-200 hover:border-green-500/50 transition-all hover:scale-105 group">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Total Earnings</p>
                <p className="text-2xl md:text-3xl font-bold text-green-600 mt-2">₹{totalEarningsSum.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg shadow-lg group-hover:scale-110 transition-all">
                <TrendingUp className="w-6 h-6 text-slate-800" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-500/10 to-blue-500/10 border-yellow-500/30 hover:border-yellow-500/50 transition-all hover:scale-105 group">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Pending Payouts</p>
                <p className="text-2xl md:text-3xl font-bold text-yellow-400 mt-2">₹{totalPendingSum.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-yellow-500 to-blue-500 rounded-lg shadow-lg group-hover:scale-110 transition-all">
                <Clock className="w-6 h-6 text-slate-800" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 border-blue-500/30 hover:border-blue-500/50 transition-all hover:scale-105 group">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Completed Payouts</p>
                <p className="text-2xl md:text-3xl font-bold text-blue-400 mt-2">₹{totalCompletedSum.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-500 rounded-lg shadow-lg group-hover:scale-110 transition-all">
                <CheckCircle className="w-6 h-6 text-slate-800" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by Name, Email, Client ID, Earning ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* Earnings List */}
      <div className="grid gap-4">
        {filteredEarnings.length === 0 ? (
          <Card className="bg-white shadow-lg border-slate-200">
            <CardContent className="py-12 text-center">
              <Wallet className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-600">No earnings records found</p>
              <p className="text-slate-400 text-sm mt-2">Add earnings for users to track their payouts</p>
            </CardContent>
          </Card>
        ) : (
          filteredEarnings.map(earning => (
            <Card key={earning.id || earning.earningId} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all animate-in fade-in slide-in-from-bottom-2 group">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Client</p>
                      <p className="font-semibold text-blue-400">#{earning.clientId}</p>
                      <p className="text-slate-800 text-sm">{earning.userName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Total Earnings</p>
                      <p className="text-green-600 font-bold text-lg">₹{earning.totalEarnings?.toLocaleString() || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Pending Payout</p>
                      <p className="text-yellow-400 font-bold">₹{earning.pendingPayout?.toLocaleString() || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Completed Payout</p>
                      <p className="text-blue-400 font-bold">₹{earning.completedPayout?.toLocaleString() || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Commission Rate</p>
                      <span className="inline-block px-2 py-1 rounded text-xs bg-blue-100 text-purple-300 font-medium">{earning.commissionRate || 10}%</span>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Payout Method</p>
                      <span className="inline-block px-2 py-1 rounded text-xs bg-cyan-500/20 text-cyan-300 font-medium">{earning.payoutMethod || 'UPI'}</span>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium mb-1">Last Payout</p>
                      <p className="text-slate-800">{earning.lastPayoutDate ? new Date(earning.lastPayoutDate).toLocaleDateString() : 'Never'}</p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => handleEdit(earning)} 
                    className="hover:bg-slate-100 transition-all hover:scale-110"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default EarningsManagement;
